import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import { useTranslations } from 'next-intl';

import { sendGTMEventEcommerce, GTMEcommerceItem } from '@/fo/api/gtm';
import { ProductStockStatus } from '@/fo/api/magento';
import {
  storeProduct,
  selectCountProductInWishlist,
  cartActions,
  wishlistActions,
  selectWishlistItemId,
  selectCartItemId,
  selectCountProductInCart,
  selectCartItemLoading
} from '@/fo/store/product';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiProductCounterInputBase } from '@/fo/ui/product/counter-input/base';
import { UiProductWishlistButton } from '@/fo/ui/product/wishlist/button';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventProductSubscribeModal } from '@/fo/util/event-product-subscribe-modal';
import { MappedProduct } from '@/fo/util/map-product-data';

import { styles } from './Actions.styles';

interface ActionsProps {
  product: MappedProduct;
  parentSku?: string;
  isAuthorized: boolean;
}

export function Actions({ product, parentSku, isAuthorized }: ActionsProps) {
  const slots = styles();
  const t = useTranslations();
  const router = useRouter();
  const cartCount = useSelector(
    storeProduct,
    selectCountProductInCart(product.sku)
  );
  const cartItemId = useSelector(storeProduct, selectCartItemId(product.sku));
  const cartLoading = useSelector(
    storeProduct,
    selectCartItemLoading(product.sku)
  );
  const wishlistSku = parentSku || product.sku;
  const wishlistCount = useSelector(
    storeProduct,
    selectCountProductInWishlist(wishlistSku)
  );
  const wishlistItemId = useSelector(
    storeProduct,
    selectWishlistItemId(wishlistSku)
  );

  const isInWishlist = wishlistCount > 0;
  const isInCart = cartCount > 0;

  const onCartChange = async (quantity: number) => {
    if (!cartItemId) return;

    if (quantity === 0) {
      await cartActions.removeCartItem(product.sku, cartItemId, product);
    } else {
      await cartActions.updateCartItem(
        product.sku,
        cartItemId,
        quantity,
        product
      );
    }
  };

  const itemGtmData: GTMEcommerceItem = {
    item_id: product.sku,
    item_name: product.name,
    price: String(product.finalPrice ?? product.basePrice ?? ''),
    item_brand: product.brand || '',
    item_category: product.series || product.brand || '',
    variant: product.volume ?? '',
    item_list_name: 'product-detail-page',
    position: 1,
    quantity: 1
  };

  const onWishlistClick = () => {
    if (isAuthorized) {
      if (isInWishlist && wishlistItemId) {
        wishlistActions.removeWishlistItems([
          { sku: wishlistSku, wishlistItemId }
        ]);
      } else {
        sendGTMEventEcommerce({
          event: 'add_to_wishlist',
          ecommerce: {
            value: itemGtmData.price,
            items: [itemGtmData]
          }
        });
        wishlistActions.addProductToWishlist(wishlistSku, 1);
      }
    } else {
      utilEventAuthModal.open('wishlist-button');
    }
  };

  const onCartClick = () => {
    if (product.stockStatus === ProductStockStatus.OutOfStock) {
      utilEventProductSubscribeModal.open(product.mindboxId);
      return;
    }

    if (isInCart) {
      router.push('/cart');
    } else {
      cartActions.addProductsToCart([
        {
          sku: product.sku,
          quantity: 1,
          product
        }
      ]);
    }
  };
  return (
    <div
      className={slots.base()}
      data-mobile-app-hidden="commerce"
    >
      {cartCount > 0 && (
        <UiProductCounterInputBase
          onDebounceChange={onCartChange}
          value={cartCount}
          loading={cartLoading && cartCount > 0}
          max={product.stockQuantity}
        />
      )}
      <UiKitButtonBase
        className={slots.addToCart()}
        onClick={onCartClick}
        loading={cartLoading && cartCount === 0}
      >
        {product.stockStatus === 'OUT_OF_STOCK' &&
          t('Find out about admission')}
        {product.stockStatus === 'IN_STOCK' && cartCount > 0 && t('In cart')}
        {product.stockStatus === 'IN_STOCK' && cartCount === 0 && t('To cart')}
      </UiKitButtonBase>
      <div className={slots.wishlist()}>
        <UiProductWishlistButton
          className={slots.wishlistIcon()}
          isActive={isInWishlist}
          onClick={onWishlistClick}
        />
      </div>
    </div>
  );
}
